/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useContext, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Col, Row } from '@thd-olt-component-react/core-ui';
import { TileGroup,Tile, TileContent, TileText } from '@one-thd/sui-atomic-components';
import { ConfirmedFilled, Clock} from '@one-thd/sui-icons';
import { ExperienceContext, useStore, useConfigService } from '@thd-nucleus/experience-context';
import { isRefinementActive, getIsMajorAppliance, getSortNameValue } from '../../product-results-helpers';
import { ProductResultsContext } from '../ProductResultsContext';
import { AvailabilityRefinement } from '../Refinements/AvailabilityRefinement';
import { getFirstNonEmptyObject } from '../../utils/searchHelpers';
import { StoreSelector } from '../StoreSelector/StoreSelector';
import '../../styles/availability-filters.scss';

const AvailabilityDimension = ({
  dimension,
  onChange,
  multiFacet,
  selectedRefinements,
  onDisplayData,
  onMultiStoreSelectionChange,
  multiStoreSelection,
  enableMultiStore,
  isMobile,
  isGetItFastOnPlp,
  shouldShowDeliverySpeedTile
}) => {  

  const isSortByDeliverySpeedTileEnabled = useConfigService('fs-prop:sort-by-delivery-speed-tile-enabled') || false;
  const [multiStoreIds, setMultiStoreIds] = useState(multiStoreSelection ? [multiStoreSelection] : []);
  const [isDSTileClicked, setIsDSTileClicked] = useState(false);
  const { storeName } = useStore({ varnish: false });
  const { deliveryZip, isConsumerApp } = useContext(ExperienceContext);
  
  const { dimensionId, label, refinements } = dimension;
  const showDeliveryOptions = { pickupToday: false, deliveryLocation: false };

  // Pick Up Today : 1z175a5
  // Free 2-Day Delivery : bwo6i
  // Same-Day Delivery : 1z175cq

  refinements.forEach((ref) => {
    // eslint-disable-next-line max-len
    if (ref.refinementKey === '1z175a5' && ref.selected) showDeliveryOptions.pickupToday = true;
    else if (ref.refinementKey === 'bwo6i' && ref.selected) showDeliveryOptions.deliveryLocation = true;
    else if (ref.refinementKey === '1z175cq' && ref.selected) showDeliveryOptions.deliveryLocation = true;
  });
  const { pickupToday, deliveryLocation } = showDeliveryOptions;

  const getRefinementId = (refinement, refinementIndex) => {
    const { refinementKey } = refinement;
    return `${refinementKey}-${refinementIndex}`;
  };

  const multiStoreCallback = ({ stores }) => {
    const checkedMultiStoreIds = stores.filter((store) => store.checked)
      .map((checkedStore) => parseInt(checkedStore.storeId, 10));

    // selected returning null after first store is selcted in dropdown
    // we are adding additional check to see if there are unchecked stores in dropdown menu
    if ((pickupToday || checkedMultiStoreIds.length < 5) && enableMultiStore && onMultiStoreSelectionChange) {
      onMultiStoreSelectionChange({ multiStoreIds: checkedMultiStoreIds, onDisplay: onDisplayData?.isStoreDisplay });
    }
    setMultiStoreIds(checkedMultiStoreIds);
  };

  const handleDelivery = () => {
    window?.LIFE_CYCLE_EVENT_BUS?.trigger('open_delivery_zip_menu');
  };

  useEffect(() => {
    const handleSortOrderChanged = (event) => {
      const { sortby, sortorder } = event?.output || {};

      const sortName = getSortNameValue({
          key: (sortby && sortby.toLowerCase()) || '',
          order: (sortorder && sortorder.toLowerCase()) || ''
        }).name;
      
      const isDeliverySpeed = sortName === 'Delivery Speed';
      setIsDSTileClicked(isDeliverySpeed);
    };
  
      window?.LIFE_CYCLE_EVENT_BUS?.on('sortorder.value', handleSortOrderChanged);
  
    return () => {
        window.LIFE_CYCLE_EVENT_BUS?.off('sortorder.value', handleSortOrderChanged);
    };
  }, []);

  const handleDSTileClick = () => {
    if (!isSortByDeliverySpeedTileEnabled) return;
    window?.LIFE_CYCLE_EVENT_BUS?.trigger('sortorder.delivery-speed',{isDeliverySpeedSortSelected: !isDSTileClicked});
    setIsDSTileClicked(!isDSTileClicked);
  };


  const isApplianceBundleRef = useRef(
    typeof (window) !== 'undefined' && window?.location?.href?.includes('N-5yc1vZ2fkpfuj')
  );
  

  return (
    <>
      <div
        key={dimensionId}
        data-instoredisplay="true"
        data-testid="availability-refinements"
        className="sui-overflow-x-auto sui-flex sui-flex-nowrap sui-gap-3"
      >
        {isSortByDeliverySpeedTileEnabled && shouldShowDeliverySpeedTile && (
          <>
            <Tile
              value={"Sort by: Delivery Speed"} 
              aria-label={"Sort by: Delivery Speed"}
              onChange={handleDSTileClick} 
              id={"sort-delivery-speed-tile"}
              key={"sort-delivery-speed-tile"}
              disabled={false}
              data-testid="availability-refinements-btn-delivery-speed"
              selected={isDSTileClicked}
              style={isDSTileClicked ? {
                backgroundColor: 'rgba(255, 255, 255, 1)'
              } : {}}
            >
              <TileContent
                alignItems={isMobile ? 'center' : 'start'}
                grow
              >
                <Clock color="primary" />
                <div className="sui-inline-block sui-pt-1 sui-whitespace-nowrap">
                  <TileText>
                    Sort by: Delivery Speed
                  </TileText>
                </div>
                {isDSTileClicked && (
                  <span className="sui-mt-1 sui-pt-1px">
                    <ConfirmedFilled size="small" />
                  </span>
                )}
              </TileContent>
            </Tile>
            <span className="sui-border-primary sui-border-solid sui-my-2" style={{borderRightWidth: '1px'}}/>
          </>
        )}
        <TileGroup
          onChange={onChange}
          aria-label={`${label} Horizontal Options`}
          grow
          key={dimensionId}
          className="sui-flex sui-flex-nowrap sui-gap-3"
        >
          {
            refinements?.map((refinement, refinementIndex) => {
              // todo: this might be a dead dependency. Look at removing it in the future.
                const id = getRefinementId(refinement, refinementIndex);
                if (
                  isApplianceBundleRef.current
                      && (refinement.label === 'Same-Day Delivery' || refinement.label === 'Next-Day Delivery')
                ) {
                  return null;
                }
                return (
                  <AvailabilityRefinement
                    dimension={dimension}
                    id={id}
                    key={id}
                    enableMultiStore={enableMultiStore}
                    refinement={refinement}
                    onMultiStoreSelectionChange={onMultiStoreSelectionChange}
                    onChange={onChange}
                    multiStoreSelection={multiStoreSelection}
                    setMultiStoreIds={setMultiStoreIds}
                    onDisplayData={onDisplayData}
                    isMobile={isMobile}
                    isGetItFastOnPlp={isGetItFastOnPlp}
                    isTileClicked={isRefinementActive(refinement.refinementKey, selectedRefinements)}
                  />
                );
              })
          }
        </TileGroup>
      </div>
      {(pickupToday || deliveryLocation) && (
        <div className="pickupOrDelivery-Wrapper">
          {pickupToday && (
            <div className="refinement__availability--pickupOrDelivery">
              <Row>
                <Col xs="10" fallback="10" className="store-selector--wrapper">
                  <StoreSelector
                    multiStoreCallback={multiStoreCallback}
                    multiStoreSelection={multiStoreSelection}
                    multiFacet={multiFacet}
                    storeName={storeName}
                    isGetItFastOnPlp={isGetItFastOnPlp}
                    refinements={refinements}
                  />
                </Col>
              </Row>
            </div>
          )}
          {(deliveryLocation && !isConsumerApp) && (
            <div className="refinement__availability--pickupOrDelivery">
              <Row>
                <Col xs="10" fallback="10" className="store-selector--wrapper" data-testid="delivery-zip">
                  <span className="store-selector__preamble"> Delivery to </span>
                  {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                  <span onClick={handleDelivery} className="store-selector__link">{deliveryZip || '...' }</span>
                </Col>
              </Row>
            </div>
          )}
        </div>
      )}
    </>
  );
};

AvailabilityDimension.propTypes = {
  dimension: PropTypes.shape({
    dimensionId: PropTypes.string,
    label: PropTypes.string,
    refinements: PropTypes.arrayOf(
      PropTypes.shape({})
    )
  }).isRequired,
  enableMultiStore: PropTypes.bool,
  multiFacet: PropTypes.bool,
  multiStoreSelection: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onDisplayData: PropTypes.shape({
    onDisplay: PropTypes.string,
    onDisplayChange: PropTypes.func,
    isStoreDisplay: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.bool
    ]),
    itemCount: PropTypes.string
  }),
  onMultiStoreSelectionChange: PropTypes.func,
  selectedRefinements: PropTypes.arrayOf(PropTypes.shape({})),
  isMobile: PropTypes.bool,
  isGetItFastOnPlp: PropTypes.bool
};

AvailabilityDimension.defaultProps = {
  enableMultiStore: PropTypes.bool,
  multiFacet: false,
  multiStoreSelection: PropTypes.string,
  onDisplayData: {
    onDisplay: 'false',
    onDisplayChange: null,
    isStoreDisplay: 'false',
    itemCount: null
  },
  onMultiStoreSelectionChange: PropTypes.func,
  selectedRefinements: null,
  isMobile: false,
  isGetItFastOnPlp: false,
  shouldShowDeliverySpeedTile: false
};

AvailabilityDimension.displayName = 'AvailabilityDimension';

export { AvailabilityDimension };
